import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Tag, TagTypeNames } from '../../../core/types/tag';
import { UntypedFormControl, Validators } from '@angular/forms';
import { TagService } from '../../../core/services/tag.service';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { Page } from '../../../core/types/page';
import { Observable, Subject } from 'rxjs';
import { SearchOptions } from '../../../core/types/search-options';
import { CreditService } from '../../../core/services/credit.service';
import { NotificationService } from '../../../core/services/notification.service';
import { AutoCompleteValidation } from '../../../core/validation/autocomplete-validation';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'itfg-add-tag',
    templateUrl: './add-tag.component.html',
    styleUrls: ['./add-tag.component.scss'],
    standalone: false
})
export class AddTagComponent implements OnInit, OnDestroy {
  public tagControl = new UntypedFormControl(null, [
    Validators.required,
    AutoCompleteValidation.isAutocompleteValidObject('notValidTag', 'id'),
  ]);
  private id: number;
  public tags: Tag[] = [];
  public tagType: TagTypeNames;
  private shownTags: Tag[] = [];
  public filteredShownTags$: Observable<Tag[]>;
  public tagTypes: typeof TagTypeNames = TagTypeNames;
  public _unsubscribe = new Subject<void>();
  private _filter(value: string): Tag[] {
    return value
      ? this.shownTags.filter((tag: Tag) =>
        tag.name.toLowerCase().includes(value.toLowerCase())
      )
      : this.shownTags.slice();
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: any,
    public dialogRef: MatDialogRef<AddTagComponent>,
    private readonly tagService: TagService,
    private readonly creditService: CreditService,
    private readonly notifications: NotificationService
  ) {
    this.tags = data.tags;
    this.id = data.id;
    this.tagType = data.type;
  }

  ngOnInit() {
    this.loadTags();

    this.tagService.onTagOrderChanged
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(orderedTags => {
        this.updateShownTags(orderedTags);
      });
  }

  loadTags() {
    this.tagService
      .getTagList$(new SearchOptions({ pageSize: 6000 }).getDTO())
      .pipe(
        map((page: Page<Tag>) => page.content),
        takeUntil(this._unsubscribe)
      )
      .subscribe({
        next: (tagList: Tag[]) => {
          const orderedTags = this.tagService.applyTagsPreferences(tagList);
          this.updateShownTags(orderedTags);
        },
        error: (error: HttpErrorResponse) => {
          console.error('Error loading tags:', error);
        }
      });
  }

  updateShownTags(orderedTags: Tag[]) {
    this.shownTags = orderedTags.filter(
      (tag: Tag) => !this.tags.map((ct: Tag) => ct.id).includes(tag.id)
    );

    this.filteredShownTags$ = this.tagControl.valueChanges.pipe(
      startWith(this.tagControl.value ? this.tagControl.value.name : ''),
      map(value => (typeof value === 'string' ? value : value?.name || '')),
      map(value => this._filter(value)),
      takeUntil(this._unsubscribe)
    );
  }

  mapTagToDisplayValue(tag: Tag) {
    return tag ? tag.name : undefined;
  }

  onSave(event: any) {
    if (this.tagControl.valid) {
      this.saveTag(this.tagControl.value)

    } else {
      this.notifications.showLocalizedErrorMessage({
        notificationText: 'forms.formSavedIsInvalid',
      });
    }
  }


  saveTag(tag: Tag): void {
    const options = {
      id: this.id,
      tagId: tag.id,
      tagValue: tag
    };

    this.dialogRef.close(options);
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}
